import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ChevronLeft, ChevronRight, MapPin, Clock, Calendar, User, Phone, Mail, MessageCircle, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import LocationPicker from './LocationPicker';
import { HoldCountdown } from './HoldCountdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const stripeLive = loadStripe(process.env.REACT_APP_STRIPE_LIVE_PUBLISHABLE_KEY);
const stripeTest = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CANCELLATION_POLICY = "Cancellations more than 24 hours before the appointment receive a full refund. Cancellations within 24 hours, or no-shows, are not refundable. Cancel by replying to the confirmation email or via WhatsApp.";

const BookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [patientType, setPatientType] = useState('');
  const [service, setService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsMessage, setSlotsMessage] = useState('');
  const [patientTimezone, setPatientTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [formData, setFormData] = useState({
    patient_name: '', patient_email: '', patient_phone: '', reason: '', reason_category: '',
    location_type: '', address: '', access_notes: '',
    yacht_at_anchor: false, yacht_pickup_point: '', yacht_contact: '',
    latitude: null, longitude: null, distance_km: null,
    telemedicine_consent: false, privacy_consent: false,
  });
  const [bookingId, setBookingId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [stripeLiveMode, setStripeLive] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [distanceSupplement, setDistanceSupplement] = useState(0);

  // Generate date options (next 30 weekdays in Ibiza time)
  const getDateOptions = () => {
    const dates = [];
    // Get current date in Ibiza time
    const ibizaNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
    for (let i = 0; i < 60 && dates.length < 30; i++) {
      const d = new Date(ibizaNow);
      d.setDate(ibizaNow.getDate() + i);
      if (d.getDay() !== 0 && d.getDay() !== 6) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        dates.push(`${yyyy}-${mm}-${dd}`);
      }
    }
    return dates;
  };

  const fetchSlots = useCallback(async () => {
    if (!selectedDate || !service) return;
    setLoadingSlots(true);
    setSlotsMessage('');
    try {
      const res = await fetch(`${API_URL}/api/bookings/available-slots?date=${selectedDate}&service=${service}`);
      const data = await res.json();
      setAvailableSlots(data.slots || []);
      if (data.message) setSlotsMessage(data.message);
      if (!data.slots?.length && !data.message) setSlotsMessage('No available slots for this date.');
    } catch { setSlotsMessage('Error loading slots.'); }
    setLoadingSlots(false);
  }, [selectedDate, service]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const getLocalTime = (ibizaTime) => {
    if (!patientTimezone || patientTimezone === 'Europe/Madrid') return null;
    try {
      const [h, m] = ibizaTime.split(':').map(Number);
      const [y, mo, d] = selectedDate.split('-').map(Number);
      const utcGuess = Date.UTC(y, mo - 1, d, h, m);
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Madrid', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      }).formatToParts(new Date(utcGuess)).map(p => [p.type, p.value]));
      const madridOffsetMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute) - utcGuess;
      const utcInstant = new Date(utcGuess - madridOffsetMs);
      const localTime = utcInstant.toLocaleString('en-US', {
        timeZone: patientTimezone, hour: '2-digit', minute: '2-digit', hour12: false
      });
      const tzAbbr = new Intl.DateTimeFormat('en-GB', {
        timeZone: patientTimezone, timeZoneName: 'short'
      }).formatToParts(utcInstant).find(p => p.type === 'timeZoneName')?.value || '';
      return `${localTime} ${tzAbbr}`;
    } catch { return null; }
  };

  const getPrice = () => {
    let base = 0;
    if (service === 'home_visit') base = 300;
    if (service === 'telemedicine') base = patientType === 'established' ? 150 : 200;
    return base + distanceSupplement;
  };

  const getBasePrice = () => {
    if (service === 'home_visit') return 300;
    if (service === 'telemedicine') return patientType === 'established' ? 150 : 200;
    return 0;
  };

  const handleSubmitDetails = async () => {
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_type: patientType, service, date: selectedDate, time: selectedTime,
          patient_name: formData.patient_name, patient_email: formData.patient_email,
          patient_phone: formData.patient_phone,
          reason: [formData.reason_category, formData.reason].filter(Boolean).join(' — '),
          privacy_consent: formData.privacy_consent,
          location_type: formData.location_type, address: formData.address,
          latitude: formData.latitude, longitude: formData.longitude,
          access_notes: formData.access_notes,
          yacht_at_anchor: formData.yacht_at_anchor,
          yacht_pickup_point: formData.yacht_pickup_point,
          yacht_contact: formData.yacht_contact,
          patient_timezone: patientTimezone,
          telemedicine_consent: formData.telemedicine_consent,
          distance_km: formData.distance_km,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create booking');
      setBookingId(data.booking_id);
      setBookingDetails(data);
      // Create checkout
      const checkoutRes = await fetch(`${API_URL}/api/bookings/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: data.booking_id, origin_url: window.location.origin }),
      });
      const checkoutData = await checkoutRes.json();
      if (!checkoutRes.ok) throw new Error(checkoutData.detail || 'Failed to create checkout');
      setStripeLive(checkoutData.live);
      setClientSecret(checkoutData.client_secret);
      setStep(5);
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!patientType;
      case 2: return !!service;
      case 3: return !!selectedDate && !!selectedTime;
      case 4:
        if (!formData.patient_name || !formData.patient_email || !formData.patient_phone || !formData.privacy_consent || !formData.reason_category) return false;
        if (service === 'home_visit' && (!formData.location_type || !formData.address || !formData.latitude)) return false;
        if (service === 'telemedicine' && !formData.telemedicine_consent) return false;
        return true;
      default: return false;
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <div className="bg-[#0f172a] text-white py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-sm text-[#94a3b8] hover:text-white flex items-center gap-1" data-testid="back-to-site">
            <ChevronLeft size={16} /> Back to site
          </button>
          <h1 className="text-lg font-semibold">Book Appointment</h1>
          <div className="w-20" />
        </div>
      </div>

      {/* Progress */}
      {step <= 5 && (
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-2">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(s => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? 'bg-[#b8860b]' : 'bg-[#e2e8f0]'}`} />
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-[#64748b]">
            <span>Patient</span><span>Service</span><span>Schedule</span><span>Details</span><span>Payment</span>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2" data-testid="booking-error">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {/* STEP 1: Patient Type */}
        {step === 1 && (
          <div data-testid="step-patient-type">
            <h2 className="text-2xl font-semibold text-[#0f172a] mb-2">Welcome</h2>
            <p className="text-[#475569] mb-6">Have you been seen by Dr. Castellon in the last 12 months?</p>
            <div className="space-y-3">
              <button data-testid="btn-established" onClick={() => setPatientType('established')}
                className={`w-full p-5 border-2 text-left transition-all ${patientType === 'established' ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0] hover:border-[#cbd5e1]'}`}>
                <div className="font-semibold text-[#0f172a]">Yes, I am an established patient</div>
                <div className="text-sm text-[#64748b] mt-1">I have been seen by Dr. Castellon within the last 12 months.</div>
              </button>
              <button data-testid="btn-new-patient" onClick={() => setPatientType('new')}
                className={`w-full p-5 border-2 text-left transition-all ${patientType === 'new' ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0] hover:border-[#cbd5e1]'}`}>
                <div className="font-semibold text-[#0f172a]">No, I am a new patient</div>
                <div className="text-sm text-[#64748b] mt-1">I have not been seen by Dr. Castellon, or it has been more than 12 months.</div>
              </button>
            </div>
            {patientType === 'established' && (
              <p className="text-sm text-[#b8860b] bg-[#fffbeb] p-3 mt-4 border border-[#fde68a]">
                The established rate applies only if you were seen in the last 12 months. Otherwise the new patient rate applies and the difference will be requested.
              </p>
            )}
          </div>
        )}

        {/* STEP 2: Service */}
        {step === 2 && (
          <div data-testid="step-service">
            <h2 className="text-2xl font-semibold text-[#0f172a] mb-2">Choose Service</h2>
            <p className="text-[#475569] mb-6">Select the type of consultation you need.</p>
            <div className="space-y-3">
              <button data-testid="btn-home-visit" onClick={() => setService('home_visit')}
                className={`w-full p-5 border-2 text-left transition-all ${service === 'home_visit' ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0] hover:border-[#cbd5e1]'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-[#0f172a] flex items-center gap-2"><MapPin size={18} /> Home Visit</div>
                    <div className="text-sm text-[#64748b] mt-1">30-minute consultation at your villa, hotel, yacht or residence</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[#0f172a]">€300</div>
                    <div className="text-xs text-[#64748b]">all patients</div>
                  </div>
                </div>
              </button>
              <button data-testid="btn-telemedicine" onClick={() => setService('telemedicine')}
                className={`w-full p-5 border-2 text-left transition-all ${service === 'telemedicine' ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0] hover:border-[#cbd5e1]'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-[#0f172a] flex items-center gap-2"><Phone size={18} /> Telemedicine</div>
                    <div className="text-sm text-[#64748b] mt-1">30-minute phone or video consultation from anywhere</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[#0f172a]">{patientType === 'established' ? '€150' : '€200'}</div>
                    <div className="text-xs text-[#64748b]">{patientType === 'established' ? 'established' : 'new patient'}</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Date & Time */}
        {step === 3 && (
          <div data-testid="step-schedule">
            <h2 className="text-2xl font-semibold text-[#0f172a] mb-2">Choose Date & Time</h2>
            <p className="text-[#475569] mb-4">Available Monday to Friday, 10:00–17:00 Ibiza time.</p>

            <label className="block text-sm font-medium text-[#0f172a] mb-2">Select Date</label>
            <select data-testid="date-select" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); setSelectedTime(''); }}
              className="w-full p-3 border border-[#e2e8f0] bg-white text-[#0f172a] mb-4 focus:outline-none focus:border-[#b8860b]">
              <option value="">Choose a date...</option>
              {getDateOptions().map(d => <option key={d} value={d}>{formatDate(d)}</option>)}
            </select>

            {selectedDate && (
              <>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">Select Time (Ibiza Time)</label>
                {loadingSlots ? (
                  <div className="text-[#64748b] py-4 text-center">Loading available times...</div>
                ) : slotsMessage ? (
                  <div className="text-[#64748b] py-4 text-center">{slotsMessage}</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                    {availableSlots.map(slot => {
                      const local = service === 'telemedicine' ? getLocalTime(slot) : null;
                      return (
                        <button key={slot} data-testid={`slot-${slot}`} onClick={() => setSelectedTime(slot)}
                          className={`p-3 border-2 text-center transition-all ${selectedTime === slot ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0] hover:border-[#cbd5e1]'}`}>
                          <div className="font-medium text-[#0f172a]">{slot}</div>
                          {local && <div className="text-xs text-[#64748b] mt-0.5">{local}</div>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {service === 'telemedicine' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#0f172a] mb-1">Your Time Zone</label>
                    <select data-testid="select-timezone" value={patientTimezone} onChange={e => setPatientTimezone(e.target.value)}
                      className="w-full p-3 border border-[#e2e8f0] bg-white text-[#0f172a] focus:outline-none focus:border-[#b8860b]">
                      {Intl.supportedValuesOf('timeZone').map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* STEP 4: Details */}
        {step === 4 && (
          <div data-testid="step-details">
            <h2 className="text-2xl font-semibold text-[#0f172a] mb-2">Your Details</h2>
            <p className="text-[#475569] mb-6">Please provide your contact information.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-1">Full Name *</label>
                <input data-testid="input-name" type="text" value={formData.patient_name} onChange={e => setFormData({...formData, patient_name: e.target.value})}
                  className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" placeholder="Your full name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-1">Email *</label>
                <input data-testid="input-email" type="email" value={formData.patient_email} onChange={e => setFormData({...formData, patient_email: e.target.value})}
                  className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-1">Phone / WhatsApp *</label>
                <input data-testid="input-phone" type="tel" value={formData.patient_phone} onChange={e => setFormData({...formData, patient_phone: e.target.value})}
                  className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" placeholder="+34 ..." />
              </div>

              {service === 'home_visit' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-1">Location Type *</label>
                    <select data-testid="select-location-type" value={formData.location_type} onChange={e => setFormData({...formData, location_type: e.target.value})}
                      className="w-full p-3 border border-[#e2e8f0] bg-white focus:outline-none focus:border-[#b8860b]">
                      <option value="">Select...</option>
                      <option value="Villa">Villa</option>
                      <option value="Hotel">Hotel</option>
                      <option value="Yacht">Yacht</option>
                      <option value="Residence">Residence</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-1">
                      {formData.location_type === 'Hotel' ? 'Hotel name and room number *' :
                       formData.location_type === 'Yacht' ? 'Marina and berth *' : 'Address *'}
                    </label>
                    <input data-testid="input-address" type="text" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}
                      className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]"
                      placeholder={formData.location_type === 'Hotel' ? 'e.g. Hotel Pacha, Room 302' : formData.location_type === 'Yacht' ? 'e.g. Marina Botafoc, Berth 45' : 'Full address'} />
                  </div>
                  {formData.location_type === 'Yacht' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-1">Is the yacht in a marina or at anchor?</label>
                        <div className="flex gap-3">
                          <button onClick={() => setFormData({...formData, yacht_at_anchor: false})}
                            className={`flex-1 p-3 border-2 ${!formData.yacht_at_anchor ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0]'}`}>In Marina</button>
                          <button onClick={() => setFormData({...formData, yacht_at_anchor: true})}
                            className={`flex-1 p-3 border-2 ${formData.yacht_at_anchor ? 'border-[#b8860b] bg-[#fffbeb]' : 'border-[#e2e8f0]'}`}>At Anchor</button>
                        </div>
                      </div>
                      {formData.yacht_at_anchor && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-[#0f172a] mb-1">Pickup point *</label>
                            <input type="text" value={formData.yacht_pickup_point} onChange={e => setFormData({...formData, yacht_pickup_point: e.target.value})}
                              className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" placeholder="Where should the tender pick up?" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#0f172a] mb-1">Contact number on board *</label>
                            <input type="tel" value={formData.yacht_contact} onChange={e => setFormData({...formData, yacht_contact: e.target.value})}
                              className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" placeholder="+34 ..." />
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-1">Access Notes *</label>
                    <textarea data-testid="input-access-notes" value={formData.access_notes} onChange={e => setFormData({...formData, access_notes: e.target.value})}
                      className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" rows={2}
                      placeholder="Gate code, villa name, who will meet the doctor, etc." required />
                  </div>
                  <LocationPicker
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    onLocationChange={(lat, lng) => setFormData(prev => ({...prev, latitude: lat, longitude: lng}))}
                    onAddressFound={(addr) => setFormData(prev => ({...prev, address: addr}))}
                    onDistanceCalculated={(km, supplement) => {
                      setFormData(prev => ({...prev, distance_km: km}));
                      setDistanceSupplement(supplement);
                    }}
                  />
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-1">Reason for Visit *</label>
                <select data-testid="select-reason" value={formData.reason_category} onChange={e => setFormData({...formData, reason_category: e.target.value})}
                  className="w-full p-3 border border-[#e2e8f0] bg-white focus:outline-none focus:border-[#b8860b] mb-2">
                  <option value="">Select a reason...</option>
                  <option value="General consultation">General consultation</option>
                  <option value="Prescription refill">Prescription refill</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="Other">Other</option>
                </select>
                <input data-testid="input-reason" type="text" value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value.slice(0, 200)})}
                  className="w-full p-3 border border-[#e2e8f0] focus:outline-none focus:border-[#b8860b]" placeholder="Additional details (optional)" maxLength={200} />
                <p className="text-xs text-[#94a3b8] mt-1">Please do not include detailed medical history here. {formData.reason.length}/200</p>
              </div>

              {service === 'telemedicine' && (
                <label className="flex items-start gap-3 p-3 border border-[#e2e8f0] cursor-pointer hover:bg-[#f8fafc]">
                  <input data-testid="checkbox-telemedicine-consent" type="checkbox" checked={formData.telemedicine_consent}
                    onChange={e => setFormData({...formData, telemedicine_consent: e.target.checked})}
                    className="mt-1 w-4 h-4 accent-[#b8860b]" />
                  <span className="text-sm text-[#475569]">
                    I have read and accept the{' '}
                    <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] underline">Telemedicine Consent & Acknowledgment</a>. *
                  </span>
                </label>
              )}

              <label className="flex items-start gap-3 p-3 border border-[#e2e8f0] cursor-pointer hover:bg-[#f8fafc]">
                <input data-testid="checkbox-privacy" type="checkbox" checked={formData.privacy_consent}
                  onChange={e => setFormData({...formData, privacy_consent: e.target.checked})}
                  className="mt-1 w-4 h-4 accent-[#b8860b]" />
                <span className="text-sm text-[#475569]">
                  I consent to the collection and processing of my personal data as described in the{' '}
                  <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] underline">Privacy Policy</a>. *
                </span>
              </label>
            </div>

            {/* Summary before payment */}
            <div className="mt-6 bg-[#f1f5f9] p-4 border border-[#e2e8f0]">
              <h3 className="font-semibold text-[#0f172a] mb-3">Booking Summary</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-[#64748b]">Service</span><span className="text-[#0f172a]">{service === 'home_visit' ? 'Home Visit' : 'Telemedicine'}</span></div>
                <div className="flex justify-between"><span className="text-[#64748b]">Date</span><span className="text-[#0f172a]">{formatDate(selectedDate)}</span></div>
                <div className="flex justify-between"><span className="text-[#64748b]">Time (Ibiza)</span><span className="text-[#0f172a]">{selectedTime}</span></div>
                <div className="flex justify-between"><span className="text-[#64748b]">Duration</span><span className="text-[#0f172a]">30 minutes</span></div>
                <div className="flex justify-between"><span className="text-[#64748b]">Patient</span><span className="text-[#0f172a] capitalize">{patientType}</span></div>
                <hr className="my-2 border-[#e2e8f0]" />
                <div className="flex justify-between"><span className="text-[#64748b]">Consultation</span><span className="text-[#0f172a]">€{getBasePrice()}</span></div>
                {distanceSupplement > 0 && (
                  <div className="flex justify-between"><span className="text-[#64748b]">Distance supplement</span><span className="text-[#0f172a]">€{distanceSupplement}</span></div>
                )}
                <hr className="my-2 border-[#e2e8f0]" />
                <div className="flex justify-between font-semibold"><span className="text-[#0f172a]">Total</span><span className="text-[#0f172a]">€{getPrice()}</span></div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-[#fffbeb] border border-[#fde68a] text-sm text-[#92400e]">
              <strong>Cancellation Policy:</strong> {CANCELLATION_POLICY}
            </div>
          </div>
        )}

        {/* STEP 5: Payment */}
        {step === 5 && clientSecret && (
          <div data-testid="step-payment">
            <h2 className="text-2xl font-semibold text-[#0f172a] mb-2">Payment</h2>
            <p className="text-[#475569] mb-4">Complete payment to confirm your appointment. Your time slot is held for 10 minutes.</p>
            <HoldCountdown expiresAt={bookingDetails?.hold_expires_at} onExpire={() => { setClientSecret(''); setSelectedTime(''); setStep(3); fetchSlots(); setError('Your 10-minute hold expired and the slot was released. Please choose a time again.'); }} />
            <div className="border border-[#e2e8f0] p-4 bg-white">
              <EmbeddedCheckoutProvider stripe={stripeLiveMode ? stripeLive : stripeTest} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {step >= 1 && step <= 4 && (
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-[#475569] hover:text-[#0f172a] transition-colors" data-testid="btn-back">
                <ChevronLeft size={18} /> Back
              </button>
            ) : <div />}
            {step < 4 ? (
              <button onClick={() => canProceed() && setStep(step + 1)} disabled={!canProceed()}
                className={`flex items-center gap-1 px-6 py-3 font-medium transition-colors ${canProceed() ? 'bg-[#0f172a] text-white hover:bg-[#b8860b]' : 'bg-[#e2e8f0] text-[#94a3b8] cursor-not-allowed'}`}
                data-testid="btn-next">
                Next <ChevronRight size={18} />
              </button>
            ) : (
              <button onClick={handleSubmitDetails} disabled={!canProceed() || submitting}
                className={`flex items-center gap-1 px-6 py-3 font-medium transition-colors ${canProceed() && !submitting ? 'bg-[#0f172a] text-white hover:bg-[#b8860b]' : 'bg-[#e2e8f0] text-[#94a3b8] cursor-not-allowed'}`}
                data-testid="btn-pay">
                {submitting ? 'Processing...' : 'Proceed to Payment'} <ChevronRight size={18} />
              </button>
            )}
          </div>
        )}

        {/* After-hours notice */}
        <div className="mt-8 p-4 bg-[#f1f5f9] border border-[#e2e8f0] text-center">
          <p className="text-sm text-[#475569] mb-2">Need a visit outside these hours or at the weekend?</p>
          <a href="https://wa.me/34635794368" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#25d366] text-white text-sm font-medium hover:bg-[#1da851] transition-colors"
            data-testid="whatsapp-afterhours">
            <MessageCircle size={16} /> Contact us on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
